import { Injectable } from "@angular/core";
import { Location } from "@angular/common";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import {
  UIAction,
  UIActionService,
  UIActionType,
  UIScreenAnalytics,
} from "@edm-sdui/sdui";
import { FirebaseHelper } from "../services/firebase.helper";

@Injectable({ providedIn: "root" })
export class SduiRedirectService {
  // Destinos que trocam de tela: a própria navegação substitui a entrada da tela que
  // redirecionou. Os demais (paywall, modal, aba nova) abrem sobre a tela anterior,
  // então é preciso sair da tela que redirecionou antes de despachar.
  private static readonly IN_APP_NAVIGATION: UIActionType[] = [
    UIActionType.INTERNAL_URL,
    UIActionType.RELOAD_INTERNAL_URL,
    UIActionType.WEBVIEW_URL,
  ];
  private static readonly FALLBACK_ROUTE: string = "/";

  private historyDepth: number = 0;
  private pendingReplace: boolean = false;
  private pendingPopstate: boolean = false;

  constructor(
    private router: Router,
    private location: Location,
    private actionService: UIActionService,
    private firebaseHelper: FirebaseHelper,
  ) {
    this.trackHistoryDepth();
  }

  // Tela SDUI que responde REDIRECT: não há o que renderizar, o action recebido é
  // executado no lugar da tela e o screen_name da cena de origem é disparado.
  execute(action: UIAction, analytics: UIScreenAnalytics | null): boolean {
    this.trackScreenView(analytics);

    const type: UIActionType | null | undefined = action?.type;
    if (!type) {
      return false;
    }

    if (type === UIActionType.REGISTER) {
      this.goToRegister();
      return true;
    }

    if (SduiRedirectService.IN_APP_NAVIGATION.includes(type)) {
      return this.actionService.execute(action, { replaceUrl: true });
    }

    this.leaveRedirectingScreen();
    // Mesmo padrão do paywall da tela de módulos: o modal abre no ciclo seguinte, com a
    // saída da tela já despachada.
    setTimeout((): void => {
      this.actionService.execute(action, { replaceUrl: true });
    }, 0);
    return true;
  }

  private trackScreenView(analytics: UIScreenAnalytics | null): void {
    const screenName: string | undefined = analytics?.screen_name;
    if (!screenName) {
      return;
    }
    this.firebaseHelper.logEvent("screen_view", { screen_name: screenName });
  }

  // Conteúdo pago sem sessão: login com retorno para a tela pedida, o mesmo caminho do
  // produto tradicional.
  private goToRegister(): void {
    this.router.navigate(["login"], {
      queryParams: { redirect: encodeURIComponent(this.router.url) },
      replaceUrl: true,
    });
  }

  // A tela que redirecionou não fica na pilha: o voltar tem que levar ao ponto anterior
  // a ela, senão a cena é refeita (always_reload) e redireciona de novo.
  private leaveRedirectingScreen(): void {
    if (this.historyDepth > 1) {
      this.location.back();
      return;
    }
    // Entrada direta (deep link, aba nova): não há ponto anterior no app para voltar.
    this.router.navigateByUrl(SduiRedirectService.FALLBACK_ROUTE, {
      replaceUrl: true,
    });
  }

  // Quantas entradas o app empilhou nesta sessão: navegação com replaceUrl não empilha
  // e o voltar do navegador desempilha. Sem essa conta, um deep link direto na tela que
  // redireciona levaria o location.back() para fora do app.
  private trackHistoryDepth(): void {
    this.router.events.subscribe((event): void => {
      if (event instanceof NavigationStart) {
        this.pendingPopstate = event.navigationTrigger === "popstate";
        this.pendingReplace =
          this.router.getCurrentNavigation()?.extras?.replaceUrl === true;
        return;
      }

      if (!(event instanceof NavigationEnd)) {
        return;
      }

      if (this.pendingPopstate) {
        this.historyDepth = Math.max(0, this.historyDepth - 1);
      } else if (!this.pendingReplace) {
        this.historyDepth += 1;
      }
    });
  }
}
